import React, { ReactNode, useCallback, useContext, useEffect, useRef, useState } from 'react';

import isEqual from 'react-fast-compare';

import { SupportedLanguages, SupportedRegions } from '@rbi-ctg/frontend';
import { IStore } from '@rbi-ctg/store';
import { CartPaymentCardType, IUserGeoFragment } from 'generated/rbi-graphql';
import useReadyQueue from 'hooks/use-ready-queue';
import { ServiceMode } from 'state/service-mode';
import { getName } from 'utils/attributes';
import { isProduction, sanityDataset } from 'utils/environment';
import { loadRegion } from 'utils/intl/region';
import LaunchDarklyHelper, {
  BooleanFlags,
  EnumFlagTypes,
  EnumFlags,
  FlagType,
  LDFlagSet,
  LDUser,
  LaunchDarklyFlag,
  LaunchDarklyFlagsObject,
  NumericFlags,
  StringFlags,
  UserAttributeUpdates,
  VariationFlags,
  initLaunchDarkly,
} from 'utils/launchdarkly';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import logger from 'utils/logger';
import noop from 'utils/noop';
import { useMemoAll } from 'utils/use-memo-all';

import { getFeatureFlagOverridesFromQueryParameters } from './feature-flag-overrides';
import safeFlagDefaults from './safe-flag-defaults';

export { LaunchDarklyFlag };

const LD_LOGIN_TIMEOUT_MS = 2000;

interface ILogin {
  cognitoId?: string;
  details: { name?: string; email: string };
}

export interface ILaunchDarklyCtx {
  attemptGetUpdatedLdFlag<F extends LaunchDarklyFlag>(
    flagKey: F,
    attributes: LDUser
  ): Promise<FlagType<F> | undefined>;
  attemptGetUpdatedLdFlag<F extends LaunchDarklyFlag>(
    flagKey: F,
    attributes: LDUser,
    defaultValue: FlagType<F>
  ): Promise<FlagType<F>>;
  flags: LaunchDarklyFlagsObject;
  flagsLoadStatus: 'cache' | 'network';
  flagOverrides: { [id: string]: boolean };
  ldUser: LDUser | null;
  /**
   * this allows us to determine whether we are receiving the correct value for
   * user-targeted flags over just the default set in LD
   */
  ldUserIsAuthenticated: boolean;
  login: (u: ILogin) => void;
  logout: () => void;
  updateCheckoutSelections: (paymentType?: CartPaymentCardType) => void;
  updateUser: (u: UserAttributeUpdates) => void;
  updateUserAttributes: (u: UserAttributeUpdates) => void;
  updateUserLiveLocation: (userGeoData?: IUserGeoFragment) => void;
  updateUserLocale: (locale: { region: SupportedRegions; language: SupportedLanguages }) => void;
  updateUserDeviceId: (deviceId: string) => void;
  updateUserStore: (store: IStore | null) => void;
  updateServiceMode: (serviceMode: ServiceMode | null) => void;
}

export const LDContext = React.createContext<ILaunchDarklyCtx>({
  attemptGetUpdatedLdFlag: () => Promise.resolve(false as any),
  flags: {},
  flagOverrides: {},
  flagsLoadStatus: 'cache',
  updateUserDeviceId: noop,
  ldUser: null,
  ldUserIsAuthenticated: false,
  login: noop,
  logout: noop,
  updateCheckoutSelections: noop,
  updateServiceMode: noop,
  updateUser: noop,
  updateUserAttributes: noop,
  updateUserLiveLocation: noop,
  updateUserLocale: noop,
  updateUserStore: noop,
});

export const useLDContext = () => useContext(LDContext);

export function useFlag<T>(flag: VariationFlags): T | null | undefined;
export function useFlag<F extends EnumFlags>(flag: F): EnumFlagTypes<F>;
export function useFlag(flag: NumericFlags): FlagType<NumericFlags>;
export function useFlag(flag: StringFlags): FlagType<StringFlags> | undefined;
export function useFlag(flag: BooleanFlags): FlagType<BooleanFlags>;
export function useFlag(flag: LaunchDarklyFlag): FlagType<LaunchDarklyFlag> {
  const { flags, flagOverrides } = useLDContext();

  if (!isProduction && flagOverrides[flag] !== undefined) {
    return flagOverrides[flag];
  }

  // @todo update code to handle missing flags or update function to accept a fallback
  return flags && flags[flag]!;
}

const GATEWAY_FLAG_PREFIXES = ['enable-gateway-', 'enable-react-native-fulfillment-service-'];
export function useGatewayFlags() {
  const { flags } = useLDContext();

  const gatewayFlags = Object.entries(flags)
    .filter(([k]) => GATEWAY_FLAG_PREFIXES.some(prefix => k.startsWith(prefix)))
    .sort((lhs, rhs) => lhs[0].localeCompare(rhs[0]))
    .map(([k, v]) => `${k}=${v}`)
    .join('&');

  return gatewayFlags;
}

/** for cases outside of react where you can't use the provider, using the provider is preferred */
export const staticLaunchDarklyFlags: { instance: LaunchDarklyFlagsObject } = {
  instance: LocalStorage.getItem(StorageKeys.LAUNCH_DARKLY_FLAGS) ?? {},
};

export function LDProvider(props: { children: ReactNode; ldFlags?: LaunchDarklyFlagsObject }) {
  const [ldUser, setLdUser] = useState<LDUser | null>(
    LocalStorage.getItem(StorageKeys.LAUNCH_DARKLY_USER_ATTRIBUTES)
  );
  const [ldClient, setLdClient] = useState<ReturnType<
    typeof LaunchDarklyHelper.getInstance
  > | null>(null);
  const [flags, setFlags] = useState<LaunchDarklyFlagsObject>(
    LocalStorage.getItem(StorageKeys.LAUNCH_DARKLY_FLAGS) ?? {}
  );
  const flagOverrides = useRef(getFeatureFlagOverridesFromQueryParameters());
  const { drainQueue, enqueueIfNotDrained } = useReadyQueue();
  const [flagsLoadStatus, setFlagsLoadStatus] = useState<ILaunchDarklyCtx['flagsLoadStatus']>(
    'cache'
  );

  // this allows us to determine whether we are receiving the correct value for
  // user-targeted flags over just the default set in LD
  const ldUserIsAuthenticated = Boolean(ldUser && !ldUser.anonymous && ldUser.email);

  // Initialize LD and update flags once we are sync'd
  useEffect(() => {
    initLaunchDarkly()
      .then(ldFlags => {
        if (!ldFlags) {
          // only set ld flags if they are non null, we might have a backup from the previous start in LocalStorage...
          return;
        }
        setFlags(oldFlags => {
          // avoid massive re-renders if flags aren't effectively changing
          if (oldFlags && JSON.stringify(ldFlags) === JSON.stringify(oldFlags)) {
            return oldFlags;
          }
          return ldFlags;
        });
        setFlagsLoadStatus('network');
        staticLaunchDarklyFlags.instance = ldFlags;
        LocalStorage.setItem(StorageKeys.LAUNCH_DARKLY_FLAGS, ldFlags);
      })
      .catch(error => {
        // we need flags or the app doesn't work right. if all else fails use safe defaults and don't cache them
        const isUsingSafeDefaults = !flags || Object.keys(flags).length === 0;
        if (isUsingSafeDefaults) {
          setFlags(safeFlagDefaults);
          staticLaunchDarklyFlags.instance = safeFlagDefaults;
        }
        logger.error({ error, message: 'LaunchDarkly init failed', isUsingSafeDefaults });
      })
      .finally(() => setLdClient(LaunchDarklyHelper.getInstance()));
  }, []);

  useEffect(() => {
    if (!ldClient) {
      return;
    }

    if (!ldClient.launchDarkly) {
      // Launch Darkly client failed to initialize and we have rendered the app without it so do nothing
      return;
    }
    drainQueue();

    // runs if a change to flag happens while the user runs the app
    // WEB ONLY: runs if a user logs in
    // RN?: Not sure if this above WEB ONLY comment applies...
    const unsubscribeListener = ldClient.addChangeListener(newFlags => {
      setFlags(state => {
        const updatedFlags = {
          ...state,
          ...newFlags,
        };

        LocalStorage.setItem(StorageKeys.LAUNCH_DARKLY_FLAGS, updatedFlags);
        staticLaunchDarklyFlags.instance = updatedFlags;
        return updatedFlags;
      });
    });

    return unsubscribeListener;
  }, [ldClient, drainQueue]);

  const updateLDUser = useCallback(async (changes: UserAttributeUpdates | null) => {
    const ldInstance = LaunchDarklyHelper.getInstance();
    let updatedFlags: LDFlagSet | undefined;
    try {
      const updatedUser = changes
        ? await ldInstance.updateCurrentUser(changes)
        : await ldInstance.clearCurrentUser();

      const newLdUser = updatedUser?.userAttributes;
      setLdUser(oldLdUser => (isEqual(oldLdUser, newLdUser) ? oldLdUser : newLdUser));

      updatedFlags = updatedUser?.newFlags;
    } catch (error) {
      // NOTE: This was crashing cypress tests because we block LD network requests
      logger.error({ error, message: 'Error updating LD user' });
    }

    // MOBILE ONLY:
    // We have to update the flags manually because ldClient's change event
    // watcher doesn't fire on login.
    if (updatedFlags) {
      setFlags(state => {
        const update = {
          ...state,
          ...updatedFlags,
        };
        LocalStorage.setItem(StorageKeys.LAUNCH_DARKLY_FLAGS, update);
        staticLaunchDarklyFlags.instance = update;
        return update;
      });
    }

    return { updatedFlags };
  }, []);

  const updateUser = useCallback(
    enqueueIfNotDrained((changes: UserAttributeUpdates) => updateLDUser(changes)),
    [updateLDUser]
  );

  const updateUserAttributes = useCallback(
    enqueueIfNotDrained((user: UserAttributeUpdates): void => {
      return updateUser({
        ...getName({ name: user.name }, { firstName: '', lastName: '' }),
        key: user.key,
        name: user.name,
        email: user.email,
      });
    }),
    [updateUser]
  );

  const updateCheckoutSelections = useCallback(
    enqueueIfNotDrained((paymentType: CartPaymentCardType | undefined): void => {
      return updateUser({
        custom: {
          paymentType: paymentType ?? '',
        },
      });
    }),
    [updateUser]
  );

  const login = useCallback(
    enqueueIfNotDrained(({ cognitoId, details: { name = '', email } }: ILogin) => {
      return updateUser({
        ...getName({ name }, { firstName: '', lastName: '' }),
        key: cognitoId,
        name,
        email,
        anonymous: false,
        custom: {
          sanityDataset: `${sanityDataset()}_${loadRegion().toLowerCase()}`,
        },
      });
    }),
    [updateUser]
  );

  const logout = useCallback(
    enqueueIfNotDrained(() => updateLDUser(null)),
    [updateLDUser]
  );

  const updateUserDeviceId = useCallback(
    enqueueIfNotDrained(deviceId => {
      return updateUser({ custom: { device_id: deviceId } });
    }),
    [updateUser]
  );

  const updateUserLocale = useCallback(
    enqueueIfNotDrained((locale: { region: SupportedRegions; language: SupportedLanguages }) => {
      return updateUser({
        custom: {
          language: locale.language,
          sanityDataset: `${sanityDataset()}_${loadRegion().toLowerCase()}`,
        },
        country: locale.region,
      });
    }),
    [updateUser]
  );

  const updateUserStore = useCallback(
    enqueueIfNotDrained((store: IStore | null) => {
      return updateUser({
        custom: {
          storeNumber: store?.number ?? '',
          storeCity: store?.physicalAddress?.city ?? '',
          storeCountry: store?.physicalAddress?.country ?? '',
          storePostalCode: store?.physicalAddress?.postalCode ?? '',
          storeStateProvince: store?.physicalAddress?.stateProvince ?? '',
          storeStateProvinceShort: store?.physicalAddress?.stateProvinceShort ?? '',
          storeFranchiseGroupName: store?.franchiseGroupName ?? '',
          storePosVendor: store?.pos?.vendor ?? '',
          storePosVersion: store?.pos?.version ?? '',
          storeVatNumber: store?.vatNumber ?? '',
        },
      });
    }),
    [updateUser]
  );

  const updateServiceMode = useCallback(
    enqueueIfNotDrained((serviceMode: ServiceMode | null) => {
      return updateUser({
        custom: {
          serviceMode: serviceMode ?? '',
        },
      });
    }),
    [updateUser]
  );

  const updateUserLiveLocation = useCallback(
    enqueueIfNotDrained((userGeoData?: IUserGeoFragment) => {
      return updateUser({
        custom: {
          liveLocationCity: userGeoData?.city ?? '',
          liveLocationState: userGeoData?.state ?? '',
          liveLocationCountry: userGeoData?.country ?? '',
        },
      });
    }),
    [updateUser]
  );

  /**
   * Attempts to get the LD user's flag value
   *
   * If the request takes longer than one second, we resolve
   * to the value already set in state. This allows us to wait on
   * LD's identify request.
   *
   */
  async function attemptGetUpdatedLdFlagFn<F extends LaunchDarklyFlag>(
    flagKey: F,
    attributes: LDUser,
    defaultFlagValue: FlagType<F>
  ): Promise<FlagType<F>>;
  async function attemptGetUpdatedLdFlagFn<F extends LaunchDarklyFlag>(
    flagKey: F,
    attributes: LDUser
  ): Promise<FlagType<F> | undefined>;
  async function attemptGetUpdatedLdFlagFn<F extends LaunchDarklyFlag>(
    flagKey: F,
    attributes: LDUser,
    defaultFlagValue?: FlagType<F>
  ): Promise<FlagType<F> | undefined> {
    return new Promise<FlagType<F> | undefined>(res => {
      const resolveWithFlagValue = (flagValue?: LaunchDarklyFlagsObject[F]) =>
        res((flagValue ?? defaultFlagValue) as FlagType<F>);

      if (!LaunchDarklyHelper.getInstance().launchDarkly) {
        return resolveWithFlagValue(flags[flagKey]);
      }
      let timedOut = false;
      let finished = false;

      setTimeout(() => {
        if (finished) {
          return;
        }
        timedOut = true;
        return resolveWithFlagValue(flags[flagKey]);
      }, LD_LOGIN_TIMEOUT_MS);

      updateLDUser(attributes).then(({ updatedFlags }) => {
        if (timedOut || !updatedFlags) {
          return;
        }
        finished = true;
        return resolveWithFlagValue(updatedFlags[flagKey]);
      });
    }).catch(() => {
      return (flags[flagKey] ?? defaultFlagValue) as FlagType<F>;
    });
  }
  const attemptGetUpdatedLdFlag = useCallback(attemptGetUpdatedLdFlagFn, [flags, updateLDUser]);

  const value = useMemoAll({
    attemptGetUpdatedLdFlag,
    flags,
    flagOverrides: flagOverrides.current,
    flagsLoadStatus,
    ldUser,
    ldUserIsAuthenticated,
    login,
    logout,
    updateCheckoutSelections,
    updateServiceMode,
    updateUser,
    updateUserAttributes,
    updateUserLiveLocation,
    updateUserLocale,
    updateUserDeviceId,
    updateUserStore,
  });

  return <LDContext.Provider value={value}>{props.children}</LDContext.Provider>;
}

export default LDContext.Consumer;
